/**
 * Histogram Chart Type Definition
 */

import {
  colorControl,
  gridControl,
  titleControl,
  xLabelControl,
  yLabelControl,
  yMinControl,
  yMaxControl,
  labelRotationControl,
  truncateXAxisLabelsControl,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  gridCode,
  axisLabelsCode,
  yAxisRangeCode,
  spineCode,
  finishCode,
} from '../shared/codeSnippets';
import { getColors } from '../shared/palettes';

const histogram = {
  id: 'histogram',
  name: 'Histogram',
  category: 'histogram',
  description: 'Visualize frequency distribution of values',
  
  // Sample data: Model inference times in milliseconds
  sampleData: [
    45, 52, 48, 61, 55, 53, 49, 58, 62, 51,
    47, 56, 59, 54, 50, 63, 57, 46, 60, 52,
    55, 48, 64, 53, 49, 57, 51, 58, 54, 50,
    67, 72, 44, 69, 55, 52, 61, 48, 56, 53,
    71, 47, 59, 54, 65, 51, 58, 49, 62, 55,
    43, 68, 52, 57, 50, 63, 54, 48, 60, 53,
    66, 51, 56, 49, 61, 55, 58, 52, 64, 47,
    70, 54, 59, 50, 62, 53, 57, 48, 65, 51,
  ],
  
  defaultConfig: {
    externalColorMode: 'single',
    color: '#4C6EE6',
    showGrid: true,
    numBins: 12,
    showEdges: true,
    xlabel: 'Value',
    ylabel: 'Frequency',
    title: '',
    yMin: '',
    yMax: '',
    labelRotation: null,
    truncateXAxisLabels: false,
  },
  
  controls: [
    colorControl,
    {
      key: 'numBins',
      label: 'Number of Bins',
      type: 'slider',
      min: 5,
      max: 25,
      step: 1,
      unit: '',
    },
    {
      key: 'showEdges',
      label: 'Show Bar Edges',
      type: 'toggle',
    },
    gridControl,
    labelRotationControl,
    truncateXAxisLabelsControl,
    xLabelControl,
    yLabelControl,
    titleControl,
    yMinControl,
    yMaxControl,
  ],
  
  generateCode: (config, dataContext, s) => {
    const hasUserData = dataContext?.isLoaded && dataContext.mapping?.value;
    const edgeColor = config.showEdges ? "'" + (s?.axisColor || '#000000') + "'" : "'none'";
    const barColor = getColors(config, 1, s)[0];

    return `${matplotlibSetup(s)}

# ======== DATA ========
${hasUserData ? `df = pd.read_csv('${dataContext.fileName}')
inference_times = df['${dataContext.mapping.value}'].dropna().tolist()` : `inference_times = [
    45, 52, 48, 61, 55, 53, 49, 58, 62, 51,
    47, 56, 59, 54, 50, 63, 57, 46, 60, 52,
    55, 48, 64, 53, 49, 57, 51, 58, 54, 50,
    67, 72, 44, 69, 55, 52, 61, 48, 56, 53,
    71, 47, 59, 54, 65, 51, 58, 49, 62, 55,
    43, 68, 52, 57, 50, 63, 54, 48, 60, 53,
    66, 51, 56, 49, 61, 55, 58, 52, 64, 47,
    70, 54, 59, 50, 62, 53, 57, 48, 65, 51,
]  # Your continuous data values`}
# ======================
${createFigure(5.5, 4.5, 300, s)}

# Create histogram
n, bins, patches = ax.hist(inference_times,
                           bins=${config.numBins || 12},
                           color='${barColor}',
                           edgecolor=${edgeColor},
                           linewidth=1.2)
${titleCode(config, s)}
${axisLabelsCode(config, s)}

# Add grid
${gridCode(config, 'y', s)}

# Set y-axis range
${yAxisRangeCode(config)}
${spineCode(s)}
${finishCode('histogram')}`;
  },
};

export default histogram;
